/**
 * Roles — Role management list page
 *
 * Navigates to /admin/roles/:id for full-page detail editor.
 * No sidebar drawer — uses FullPageDetailTemplate for detail view.
 */

import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ListPageTemplate from "templates/ListPageTemplate";
import KDBox from "components/KDBox";
import KDButton from "components/KDButton";
import useRoles from "hooks/useRoles";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";

function Roles() {
  const t = useT();
  const ability = useAbility();
  const navigate = useNavigate();
  const { roles, loading, error } = useRoles({ autoFetch: true });

  const canCreate = ability.can(ACTIONS.CREATE, SUBJECTS.ROLE);
  const canEdit = ability.can(ACTIONS.UPDATE, SUBJECTS.ROLE);

  const columns = useMemo(
    () => [
      {
        field: "name",
        header: t("roles.name"),
        width: "20%",
      },
      {
        field: "description",
        header: t("roles.description_label"),
        width: "30%",
      },
      {
        field: "isSystem",
        header: t("roles.type"),
        width: "12%",
        render: (val) => (
          <Chip
            label={val ? t("roles.system") : t("roles.custom")}
            size="small"
            color={val ? "primary" : "default"}
            variant="outlined"
          />
        ),
      },
      {
        field: "isActive",
        header: t("roles.status"),
        width: "12%",
        render: (val) => (
          <Chip
            label={val !== false ? t("common.active") : t("common.inactive")}
            size="small"
            color={val !== false ? "success" : "default"}
          />
        ),
      },
      {
        field: "permissionsCount",
        header: t("roles.permissions"),
        width: "11%",
        align: "center",
        render: (_, row) => (row.permissions?.length || 0),
      },
      {
        field: "actions",
        header: t("common.actions"),
        width: "15%",
        align: "right",
        sortable: false,
        render: (_, row) => (
          <KDBox display="flex" justifyContent="flex-end" gap={0.5}>
            {canEdit && (
              <Tooltip title={t("common.edit")}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/roles/${row.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </KDBox>
        ),
      },
    ],
    [t, canEdit, navigate]
  );

  const handleRowClick = useCallback(
    (row) => navigate(`/admin/roles/${row.id}`),
    [navigate]
  );

  return (
    <ListPageTemplate
        title={t("nav.roles")}
        columns={columns}
        rows={roles}
        loading={loading}
        error={error}
        emptyMessage={t("roles.empty")}
        onRowClick={handleRowClick}
        searchable
        searchPlaceholder={t("roles.search")}
        defaultSortField="name"
        headerColor="info"
        defaultVisibleFields={["name", "description", "isSystem", "isActive", "permissionsCount", "actions"]}
        extraToolbarActions={
          canCreate ? (
            <KDButton
              variant="contained"
              size="small"
              onClick={() => navigate("/admin/roles/new")}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
              }}
            >
              <AddIcon sx={{ mr: 0.5 }} /> {t("roles.create")}
            </KDButton>
          ) : null
        }
      />
  );
}

export default Roles;
